import { useCallback, useEffect, useState } from "react";
import { decodeJwt } from "jose";
import { getWidgetBaseUrl, resolveWidgetUrl } from "@/lib/env";
const ONE_MINUTE_IN_MILLISECONDS = 60 * 1000;
export default function useConnectionDetails(appConfig) {
    // Generate room connection details, including:
    //   - A random Room name
    //   - A random Participant name
    //   - An Access Token to permit the participant to join the room
    //   - The URL of the LiveKit server to connect to
    //
    // In real-world application, you would likely allow the user to specify their
    // own participant name, and possibly to choose from existing rooms to join.
    const [connectionDetails, setConnectionDetails] = useState(null);
    const fetchConnectionDetails = useCallback(async () => {
        var _a, _b;
        setConnectionDetails(null);
        const baseUrl = getWidgetBaseUrl();
        const url = resolveWidgetUrl((_a = process.env.NEXT_PUBLIC_CONN_DETAILS_ENDPOINT) !== null && _a !== void 0 ? _a : "/api/connection-details", baseUrl);
        let data;
        try {
            const res = await fetch(url, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-Sandbox-Id": (_b = appConfig.sandboxId) !== null && _b !== void 0 ? _b : "",
                },
                body: JSON.stringify({
                    room_config: appConfig.agentName
                        ? {
                            agents: [{ agent_name: appConfig.agentName }],
                        }
                        : undefined,
                }),
            });
            data = await res.json();
        }
        catch (error) {
            console.error("Error fetching connection details:", error);
            throw new Error("Error fetching connection details!");
        }
        setConnectionDetails(data);
        return data;
    }, [appConfig.agentName, appConfig.sandboxId]);
    useEffect(() => {
        fetchConnectionDetails();
    }, [fetchConnectionDetails]);
    const isConnectionDetailsExpired = useCallback(() => {
        const token = connectionDetails === null || connectionDetails === void 0 ? void 0 : connectionDetails.participantToken;
        if (!token) {
            return true;
        }
        const jwtPayload = decodeJwt(token);
        if (!jwtPayload.exp) {
            return true;
        }
        const expiresAt = new Date(jwtPayload.exp * 1000 - ONE_MINUTE_IN_MILLISECONDS);
        return expiresAt <= new Date();
    }, [connectionDetails === null || connectionDetails === void 0 ? void 0 : connectionDetails.participantToken]);
    const existingOrRefreshConnectionDetails = useCallback(async () => {
        if (isConnectionDetailsExpired() || !connectionDetails) {
            return fetchConnectionDetails();
        }
        else {
            return connectionDetails;
        }
    }, [connectionDetails, fetchConnectionDetails, isConnectionDetailsExpired]);
    return {
        connectionDetails,
        refreshConnectionDetails: fetchConnectionDetails,
        existingOrRefreshConnectionDetails,
    };
}
